import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Page } from 'react-pdf';
import type { TextContent } from 'react-pdf';
import {
  canDrawRegions,
  findPassages,
  renderHighlightedText,
  TextLayerItem,
} from './textHighlight';
import { PdfPageHighlight } from './types';
import styles from './PDFViewer.module.scss';

type Props = {
  pageNumber: number;
  scale: number;
  width?: number;
  // Passages to highlight on this page, if any.
  highlight?: PdfPageHighlight;
  onLoadSuccess: (page: { width: number; height: number }) => void;
  onRenderSuccess: () => void;
  // Called with the first highlighted element (a text-layer mark, else a
  // region) whenever the highlights (re)render — a scroll target.
  onHighlightReady?: (pageNumber: number, element: HTMLElement) => void;
};

// A react-pdf Page that highlights passages: exact characters in the text
// layer when the passage is found there, else (scans, no text layer) the
// block-sized regions the BE located it in — only when they were measured on a
// page shaped like the rendered one.
export default function HighlightedPage({
  pageNumber,
  scale,
  width,
  highlight,
  onLoadSuccess,
  onRenderSuccess,
  onHighlightReady,
}: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [textItems, setTextItems] = useState<TextLayerItem[] | null>(null);
  const [pageRatio, setPageRatio] = useState<{
    width: number;
    height: number;
  } | null>(null);
  // Bumped on every text-layer render, so the scroll target is re-reported
  // once the marks are actually in the DOM.
  const [textLayerRenders, setTextLayerRenders] = useState(0);

  const passages = highlight?.passages;
  const found = useMemo(
    () =>
      passages?.length && textItems
        ? findPassages(
            textItems,
            passages.map((passage) => passage.text),
          )
        : null,
    [passages, textItems],
  );

  const itemHighlights = found?.highlights;
  const customTextRenderer = useMemo(
    () =>
      itemHighlights?.size
        ? ({ str, itemIndex }: { str: string; itemIndex: number }) =>
            renderHighlightedText(
              str,
              itemHighlights.get(itemIndex),
              styles.textHighlight,
            )
        : undefined,
    [itemHighlights],
  );

  // Regions of the passages the text layer doesn't hold.
  const regions = useMemo(() => {
    if (!found?.unmatched.length || !pageRatio) return [];
    if (
      !canDrawRegions(highlight?.aspectRatio, pageRatio.width, pageRatio.height)
    ) {
      return [];
    }
    return found.unmatched.flatMap(
      (passageIndex) => passages?.[passageIndex]?.regions ?? [],
    );
  }, [found, highlight?.aspectRatio, pageRatio, passages]);

  const handleGetTextSuccess = useCallback(
    // Same indexes as `items` — `customTextRenderer`'s `itemIndex` refers to
    // them — with marked content (no text) kept as empty entries.
    ({ items }: TextContent) =>
      setTextItems(items.map((item) => ('str' in item ? item : {}))),
    [],
  );
  const handleRenderTextLayerSuccess = useCallback(
    () => setTextLayerRenders((count) => count + 1),
    [],
  );
  const handleLoadSuccess = useCallback(
    (page: { width: number; height: number }) => {
      setPageRatio({ width: page.width, height: page.height });
      onLoadSuccess(page);
    },
    [onLoadSuccess],
  );

  const hasPassages = !!passages?.length;

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!hasPassages || !onHighlightReady || !wrap) return;
    const target =
      wrap.querySelector<HTMLElement>(`mark.${styles.textHighlight}`) ??
      wrap.querySelector<HTMLElement>(`.${styles.regionHighlight}`);
    if (target) onHighlightReady(pageNumber, target);
  }, [hasPassages, onHighlightReady, pageNumber, regions, textLayerRenders]);

  return (
    <div ref={wrapRef}>
      <Page
        pageNumber={pageNumber}
        scale={scale}
        width={width}
        onLoadSuccess={handleLoadSuccess}
        onRenderSuccess={onRenderSuccess}
        // Pages without highlights skip the text bookkeeping entirely.
        onGetTextSuccess={hasPassages ? handleGetTextSuccess : undefined}
        onRenderTextLayerSuccess={
          hasPassages ? handleRenderTextLayerSuccess : undefined
        }
        customTextRenderer={customTextRenderer}
      >
        {regions.map((region) => (
          <div
            key={`${region.x}-${region.y}-${region.width}-${region.height}`}
            className={styles.regionHighlight}
            style={{
              left: `${region.x * 100}%`,
              top: `${region.y * 100}%`,
              width: `${region.width * 100}%`,
              height: `${region.height * 100}%`,
            }}
          />
        ))}
      </Page>
    </div>
  );
}
